import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";

const inputCls =
    "w-full bg-transparent border border-line px-4 py-3 text-sm text-white placeholder:text-white/30 focus:border-[#FF5C00] focus:outline-none transition-colors duration-300 rounded-none";

const CartDrawer = () => {
    const { items, setQty, removeItem, clear, open, setOpen } = useCart();
    const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", delivery_address: "", notes: "" });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/orders", {
                ...form,
                items: items.map((i) => ({ product_id: i.id, name: i.name, quantity: i.qty })),
            });
            setSuccess(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not submit enquiry. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const closeAndReset = () => {
        setOpen(false);
        if (success) {
            clear();
            setForm({ name: "", company: "", email: "", phone: "", delivery_address: "", notes: "" });
            setSuccess(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => (v ? setOpen(true) : closeAndReset())}>
            <SheetContent
                side="right"
                data-testid="cart-drawer"
                className="w-full sm:max-w-lg bg-[#0A0A0A] border-l border-line text-white p-0 flex flex-col rounded-none [&>button]:text-white"
            >
                <SheetHeader className="px-6 py-5 border-b border-line">
                    <SheetTitle className="font-heading font-extrabold uppercase tracking-tight text-xl text-white">
                        Order Enquiry
                        <span className="font-mono text-xs text-[#FF5C00] ml-3 tracking-[0.2em]">
                            {items.length} item{items.length === 1 ? "" : "s"}
                        </span>
                    </SheetTitle>
                </SheetHeader>

                {success ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-10 text-center" data-testid="cart-success-message">
                        <CheckCircle2 className="w-12 h-12 text-[#FF5C00]" />
                        <h3 className="mt-6 font-heading font-extrabold uppercase text-2xl tracking-tight">Enquiry Received</h3>
                        <p className="mt-4 text-white/70 text-sm leading-relaxed max-w-xs">
                            Thanks for your enquiry. Our team will review your order and get back to you shortly.
                        </p>
                        <button
                            data-testid="cart-done-button"
                            onClick={closeAndReset}
                            className="mt-10 px-10 h-12 bg-[#FF5C00] text-white font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors duration-300"
                        >
                            Done
                        </button>
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-10 text-center" data-testid="cart-empty-state">
                        <p className="font-mono text-xs tracking-[0.25em] uppercase text-white/40">No products added yet</p>
                        <Link
                            to="/products"
                            data-testid="cart-browse-products-button"
                            onClick={() => setOpen(false)}
                            className="mt-8 px-10 h-12 flex items-center bg-[#FF5C00] text-white font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors duration-300"
                        >
                            Browse Products
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={submit} className="flex-1 flex flex-col overflow-hidden" data-testid="order-form">
                        <div className="flex-1 overflow-y-auto" data-lenis-prevent>
                            <ul className="divide-y divide-line border-b border-line">
                                {items.map((i) => (
                                    <li key={i.id} data-testid={`cart-item-${i.slug}`} className="flex gap-4 p-4">
                                        <div className="w-16 h-16 bg-white shrink-0 overflow-hidden">
                                            <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-heading font-bold text-sm leading-tight truncate">{i.name}</p>
                                            <p className="font-mono text-[10px] text-white/40 mt-1 uppercase tracking-wider">{i.category} — standard size</p>
                                            <div className="mt-3 flex items-center gap-3">
                                                <button type="button" data-testid={`cart-qty-dec-${i.slug}`} onClick={() => setQty(i.id, i.qty - 1)} className="w-7 h-7 border border-line flex items-center justify-center hover:border-[#FF5C00] transition-colors" aria-label="Decrease quantity">
                                                    <Minus className="w-3 h-3" />
                                                </button>
                                                <span className="font-mono text-sm w-6 text-center" data-testid={`cart-qty-${i.slug}`}>{i.qty}</span>
                                                <button type="button" data-testid={`cart-qty-inc-${i.slug}`} onClick={() => setQty(i.id, i.qty + 1)} className="w-7 h-7 border border-line flex items-center justify-center hover:border-[#FF5C00] transition-colors" aria-label="Increase quantity">
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </div>
                                        <button type="button" data-testid={`cart-remove-${i.slug}`} onClick={() => removeItem(i.id)} className="self-start p-1 text-white/40 hover:text-[#FF5C00] transition-colors" aria-label="Remove item">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </li>
                                ))}
                            </ul>

                            <div className="p-6 space-y-4">
                                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">Contact & Delivery Details</p>
                                <input required data-testid="order-name-input" placeholder="Name *" value={form.name} onChange={update("name")} className={inputCls} />
                                <input data-testid="order-company-input" placeholder="Company" value={form.company} onChange={update("company")} className={inputCls} />
                                <div className="grid grid-cols-2 gap-4">
                                    <input required type="email" data-testid="order-email-input" placeholder="Email *" value={form.email} onChange={update("email")} className={inputCls} />
                                    <input required data-testid="order-phone-input" placeholder="Phone *" value={form.phone} onChange={update("phone")} className={inputCls} />
                                </div>
                                <input required data-testid="order-address-input" placeholder="Delivery address *" value={form.delivery_address} onChange={update("delivery_address")} className={inputCls} />
                                <textarea data-testid="order-notes-input" placeholder="Additional notes" rows={3} value={form.notes} onChange={update("notes")} className={`${inputCls} resize-none`} />
                            </div>
                        </div>
                        <div className="p-6 border-t border-line">
                            <button
                                type="submit"
                                data-testid="order-submit-button"
                                disabled={submitting}
                                className="w-full h-14 bg-[#FF5C00] text-white font-mono text-xs tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50"
                            >
                                {submitting ? "Submitting…" : "Submit Order Enquiry"}
                            </button>
                        </div>
                    </form>
                )}
            </SheetContent>
        </Sheet>
    );
};

export default CartDrawer;
